import {
  Body1,
  Button,
  Skeleton as FluentSkeleton,
  SkeletonItem,
  Spinner,
  Subtitle2,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ErrorCircleRegular,
  LockClosedRegular,
  MailInboxRegular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';

import { ApiError } from '@/api/client';

const useStyles = makeStyles({
  frame: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '8px',
    ...shorthands.padding('48px', '24px'),
    textAlign: 'center',
  },
  icon: { fontSize: '28px', color: tokens.colorNeutralForeground3 },
  iconDanger: { fontSize: '28px', color: tokens.colorPaletteRedForeground1 },
  body: { maxWidth: '480px', color: tokens.colorNeutralForeground3 },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '8px',
    ...shorthands.padding('48px'),
  },
  requestId: {
    ...shorthands.padding('2px', '6px'),
    backgroundColor: tokens.colorNeutralBackground3,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  stack: { display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: '12px' },
});

function Frame({ icon, title, body }: { icon: ReactNode; title: string; body?: ReactNode }) {
  const styles = useStyles();
  return (
    <div className={styles.frame}>
      {icon}
      <Subtitle2>{title}</Subtitle2>
      {body && <Body1 className={styles.body}>{body}</Body1>}
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body?: ReactNode }) {
  const styles = useStyles();
  return <Frame icon={<MailInboxRegular className={styles.icon} />} title={title} body={body} />;
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  const styles = useStyles();
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      <Spinner size="small" label={`${label}…`} />
    </div>
  );
}

export function PermissionDenied() {
  const styles = useStyles();
  return (
    <Frame
      icon={<LockClosedRegular className={styles.icon} />}
      title="You do not have access to this page"
      body="This area is restricted to administrators. Ask an admin if you need access."
    />
  );
}

/**
 * A 403 renders PermissionDenied, never a blank page. The request id is shown so
 * support can find the exact log line.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const styles = useStyles();
  if (error instanceof ApiError && error.status === 403) return <PermissionDenied />;

  const message = error instanceof ApiError ? error.friendlyMessage : 'Something went wrong.';
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <Frame
      icon={<ErrorCircleRegular className={styles.iconDanger} />}
      title={message}
      body={
        <span className={styles.stack}>
          {requestId && <code className={styles.requestId}>Request {requestId}</code>}
          {onRetry && (
            <Button size="small" onClick={onRetry}>
              Try again
            </Button>
          )}
        </span>
      }
    />
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <FluentSkeleton className={className}>
      <SkeletonItem />
    </FluentSkeleton>
  );
}
